// /admin/photo-vetting/:residentId — admin toggles which uploaded photos
// of a resident's yard may appear publicly on /keep-mowing.
// Linked from the Continuations queue when a crowdfund is approved and
// the resident has allow_photo_on_continuation='yes'.

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AdminNav } from '../components/AdminNav';

interface ApiData {
  resident: { id: number; name: string; city: string; allow_photo_on_continuation: string };
  all_photos: string[];
  public_photo_keys: string[];
}

export function AdminPhotoVettingPage() {
  const { residentId } = useParams<{ residentId: string }>();
  const [data, setData] = useState<ApiData | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`/api/admin/photo-vetting?resident_id=${residentId}`, { credentials: 'include' });
      const j = await res.json() as { ok: boolean; data?: ApiData; error?: string };
      if (!j.ok) throw new Error(j.error || 'fetch failed');
      setData(j.data!);
      setSelected(new Set(j.data!.public_photo_keys));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Load failed');
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [residentId]);

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const save = async () => {
    if (!data) return;
    setSaving(true); setMsg(null);
    try {
      const res = await fetch('/api/admin/photo-vetting', {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resident_id: data.resident.id,
          public_photo_keys: Array.from(selected),
        }),
      });
      const j = await res.json() as { ok: boolean; error?: string };
      if (!j.ok) throw new Error(j.error || 'Save failed');
      setMsg(`Saved. ${selected.size} photo${selected.size === 1 ? '' : 's'} now public.`);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Save failed');
    } finally { setSaving(false); }
  };

  const dirty = useMemo(() => {
    if (!data) return false;
    if (selected.size !== data.public_photo_keys.length) return true;
    for (const k of data.public_photo_keys) if (!selected.has(k)) return true;
    return false;
  }, [data, selected]);

  if (loading) {
    return (
      <>
        <AdminNav />
        <div className="max-w-4xl mx-auto px-6 py-16 text-forest/60 italic">Loading...</div>
      </>
    );
  }
  if (error || !data) {
    return (
      <>
        <AdminNav />
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="bg-rose/10 border-l-4 border-rose p-4 text-rose text-sm">{error || 'Not found.'}</div>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminNav />
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12">
        <Link to="/admin/continuations" className="text-rose hover:underline text-sm">← back to continuations</Link>
        <div className="text-xs tracking-[0.25em] uppercase text-rose mt-6 mb-3">Admin · Photo vetting</div>
        <h1 className="text-4xl text-forest mb-2 font-display" style={{ fontVariationSettings: '"opsz" 144' }}>
          {data.resident.name}
        </h1>
        <p className="text-forest/70 mb-2">{data.resident.city}</p>
        <p className="text-sm text-forest/65 mb-6 max-w-2xl">
          Toggle which photos may appear on the public /keep-mowing detail
          page for this household. Photos only appear publicly if the
          resident has opted into photo display (currently:{' '}
          <strong className={data.resident.allow_photo_on_continuation === 'yes' ? 'text-forest' : 'text-rose'}>
            {data.resident.allow_photo_on_continuation}
          </strong>
          ).
        </p>

        {data.all_photos.length === 0 ? (
          <p className="text-forest/55 italic">No photos uploaded by this household.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {data.all_photos.map((key) => {
                const on = selected.has(key);
                return (
                  <button
                    key={key} type="button" onClick={() => toggle(key)}
                    className={`relative block border-2 transition-all ${
                      on ? 'border-rose' : 'border-forest/15 opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={`/api/admin/photo/${encodeURIComponent(key)}`}
                      alt="Yard"
                      className="w-full h-32 object-cover"
                    />
                    <div className={`absolute top-2 right-2 w-6 h-6 flex items-center justify-center text-xs font-medium ${
                      on ? 'bg-rose text-cream' : 'bg-cream text-forest/55'
                    }`}>
                      {on ? '✓' : '+'}
                    </div>
                    <div className="absolute bottom-2 left-2 text-[10px] tracking-[0.15em] uppercase bg-cream/90 text-forest px-1.5 py-0.5">
                      {on ? 'public' : 'private'}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-3 sticky bottom-4 bg-cream-alt border border-forest/15 p-3">
              <span className="text-sm text-forest/75">
                <strong>{selected.size}</strong> of {data.all_photos.length} marked public
              </span>
              <button
                type="button" disabled={saving || !dirty} onClick={save}
                className="ml-auto text-xs tracking-[0.15em] uppercase bg-forest text-cream px-5 py-2 hover:bg-rose transition-colors disabled:opacity-40"
              >
                {saving ? 'Saving...' : dirty ? 'Save selection' : 'No changes'}
              </button>
              {msg && <span className="text-sm text-forest/70">{msg}</span>}
            </div>
          </>
        )}
      </div>
    </>
  );
}
